import { Injectable, inject } from '@angular/core';
import {
  init,
  identify,
  sendEvent,
  reportError,
  AttributeValueType,
} from '@dash0/sdk-web';
import { BdUserTracker } from '@bindoc-projects/util-domain-lib';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { AccountInfo } from '@azure/msal-node';

// Cap the pre-init error buffer so a never-consenting visitor (or a
// pre-init crash loop) can't bloat memory. The Dash0 SDK already dedups
// downstream by message+stack+location.
const PENDING_ERRORS_LIMIT = 50;

type PendingException = {
  error: unknown;
  customProperties?: { [key: string]: unknown };
};

@Injectable({
  providedIn: 'root',
})
export class BdDash0TrackingService extends BdUserTracker {
  private readonly _environmentService = inject(BdAppEnvironmentService);

  public name = 'BdDash0TrackingService';

  // Errors raised between page load and `init()` running. The Mapbox
  // "Invalid LngLat" crash from BDA-5426 fires during afterNextRender, which
  // can happen before the CCM consent seed completes and triggers init —
  // dropping the most useful root-cause errors. Buffer them and flush once
  // the SDK is initialised so they reach Dash0 from the next request onward.
  private _pendingExceptions: PendingException[] = [];

  public init(accountInfo: AccountInfo | null): void {
    // Read as optional: BdAppEnvironmentService.load() throws when the key is
    // absent from sessionStorage, which would surface as
    // "BD_DASH0_ENDPOINT_URL is missing in storage" the moment the user
    // accepts the Dash0 Web Monitoring consent on an app whose config does not
    // include the BD_DASH0_* variables (e.g. older cached config.json,
    // app-config.ts missing the variableKeys entry, or an empty dev value).
    // Treat missing/empty config as "tracking disabled" and skip init silently.
    const endpointUrl = this._environmentService.load(
      'BD_DASH0_ENDPOINT_URL',
      true,
    );
    const authToken = this._environmentService.load(
      'BD_DASH0_AUTH_TOKEN',
      true,
    );
    const dataset = this._environmentService.load('BD_DASH0_DATASET', true);

    if (!endpointUrl || !authToken) {
      return;
    }

    const serviceName =
      this._environmentService.load('BD_DASH0_SERVICE_NAME', true) ||
      'bindoc-frontend';

    init({
      serviceName,
      endpoint: {
        url: endpointUrl,
        authToken,
        ...(dataset ? { dataset } : {}),
      },
    });

    if (accountInfo) {
      identify(accountInfo.localAccountId);
    }

    this.isEnabled = true;
    this._flushPendingExceptions();
  }

  destroy(): void {
    this.isEnabled = false;
  }

  addAuthenticatedUserContext(accountInfo: AccountInfo): void {
    if (this.isEnabled && accountInfo) {
      identify(accountInfo.localAccountId);
    }
  }

  removeAuthenticatedUserContext(): void {
    if (this.isEnabled) {
      identify();
    }
  }

  trackException(error: unknown, customProperties?: { [key: string]: unknown }): void {
    if (this.isEnabled) {
      this._reportException(error, customProperties);
      return;
    }

    if (this._pendingExceptions.length >= PENDING_ERRORS_LIMIT) {
      return;
    }
    this._pendingExceptions.push({ error, customProperties });
  }

  trackEvent(event: string, customProperties?: { [key: string]: unknown }): void {
    if (this.isEnabled) {
      sendEvent(event, {
        attributes: customProperties as Record<string, AttributeValueType>,
      });
    }
  }

  private _reportException(
    error: unknown,
    customProperties?: { [key: string]: unknown },
  ): void {
    reportError(error instanceof Error ? error : String(error), {
      attributes: customProperties as Record<string, AttributeValueType>,
    });
  }

  private _flushPendingExceptions(): void {
    if (this._pendingExceptions.length === 0) {
      return;
    }
    const pending = this._pendingExceptions;
    this._pendingExceptions = [];
    for (const { error, customProperties } of pending) {
      this._reportException(error, customProperties);
    }
  }
}
